import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import {
  ClioService,
  LloFunnelReportItem,
  LloFunnelReportRequest,
  OpenAPI,
  UsersModel,
} from "../../../api";
import FilterableSelect from "../../../components/basic/FilterableSelect";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

const MySwal = withReactContent(Swal);
const TEAM_MEMBER_ALL = "";

type RowWithSpans = LloFunnelReportItem & {
  rowNumber?: number;
  groupRowSpan?: number;
  skipCaseColumns?: boolean;
};

function computeCaseGroupSpans(rows: LloFunnelReportItem[]): RowWithSpans[] {
  const out: RowWithSpans[] = [];
  let i = 0;
  let caseNumber = 0;

  while (i < rows.length) {
    const caseName = rows[i]?.caseName ?? "";
    let j = i + 1;
    while (j < rows.length && (rows[j]?.caseName ?? "") === caseName) j++;

    const span = j - i;
    caseNumber += 1;

    for (let k = i; k < j; k++) {
      out.push({
        ...rows[k],
        rowNumber: k === i ? caseNumber : undefined,
        groupRowSpan: k === i ? span : undefined,
        skipCaseColumns: k !== i,
      });
    }

    i = j;
  }

  return out;
}

export default function LloFunnelReport() {
  const [reportData, setReportData] = useState<LloFunnelReportItem[]>([]);
  const [usersList, setUsersList] = useState<UsersModel[]>([]);
  const [teamMemberName, setTeamMemberName] = useState(TEAM_MEMBER_ALL);
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    ClioService.getApiClioClioUsers().then((users) => {
      setUsersList(users.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")));
    });
  }, []);

  const buildRequest = useCallback(
    (): LloFunnelReportRequest => {
      const selectedUser =
        teamMemberName && teamMemberName !== TEAM_MEMBER_ALL
          ? usersList.find((user) => user.name === teamMemberName)
          : null;

      return {
        teamMemberId: selectedUser?.id ?? null,
        teamMemberName: selectedUser?.name ?? null,
        startDate: startDate ? dayjs(startDate).format("YYYY-MM-DD") : null,
        endDate: endDate ? dayjs(endDate).format("YYYY-MM-DD") : null,
        searchTerm: searchTerm.trim() || null,
      };
    },
    [endDate, searchTerm, startDate, teamMemberName, usersList]
  );

  const loadReport = useCallback(() => {
    setLoading(true);
    ClioService.postApiClioLloFunnelReport(buildRequest())
      .then((data) => {
        setReportData(data);
        setPage(0);
      })
      .catch(() =>
        MySwal.fire({
          icon: "error",
          title: <p>Error loading LLO funnel report.</p>,
        })
      )
      .finally(() => setLoading(false));
  }, [buildRequest]);

  const caseGroups = useMemo(() => {
    const groups: LloFunnelReportItem[][] = [];
    let currentGroup: LloFunnelReportItem[] = [];
    let currentCaseName = "";

    reportData.forEach((row) => {
      if (row.caseName !== currentCaseName) {
        if (currentGroup.length > 0) {
          groups.push(currentGroup);
        }
        currentGroup = [row];
        currentCaseName = row.caseName;
      } else {
        currentGroup.push(row);
      }
    });

    if (currentGroup.length > 0) {
      groups.push(currentGroup);
    }

    return groups;
  }, [reportData]);

  const paginatedRows = useMemo(() => {
    const pageGroups = caseGroups.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);
    return computeCaseGroupSpans(pageGroups.flat());
  }, [caseGroups, page, rowsPerPage]);

  const uniqueCaseCount = caseGroups.length;

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const response = await fetch(`${OpenAPI.BASE}/api/Clio/lloFunnelReport/download`, {
        method: "POST",
        headers: {
          Authorization: (OpenAPI.HEADERS as Record<string, string>)?.Authorization,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(buildRequest()),
      });

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = `LloFunnelReport-${dayjs().format("YYYY-MM-DD")}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch {
      MySwal.fire({
        icon: "error",
        title: <p>Error downloading LLO funnel report.</p>,
      });
    } finally {
      setDownloading(false);
    }
  };

  const matterGroupCellStyle = {
    borderBottom: "2px solid #cbd5e1",
    verticalAlign: "top" as const,
  };

  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight={600}>
          LLO Case Funnel Overview
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Limited Litigation tasks with descriptions modified from the standard template.
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
          <FilterableSelect
            label="Team Member"
            value={teamMemberName || TEAM_MEMBER_ALL}
            options={usersList.map((user) => ({
              value: user.name!,
              label: user.name!,
            }))}
            onChange={(value) =>
              setTeamMemberName(
                value === undefined || value === TEAM_MEMBER_ALL ? TEAM_MEMBER_ALL : String(value)
              )
            }
            allOptionLabel="Select None"
            allOptionValue={TEAM_MEMBER_ALL}
            searchPlaceholder="Type to filter team members..."
            minWidth={260}
          />
          <DatePicker
            label="Task Due Date From"
            value={startDate}
            onChange={(value) => setStartDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 220 } } }}
          />
          <DatePicker
            label="Task Due Date To"
            value={endDate}
            onChange={(value) => setEndDate(value)}
            slotProps={{ textField: { size: "small", sx: { width: 220 } } }}
          />
          <TextField
            placeholder="Search cases, tasks, descriptions..."
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
            sx={{ width: { xs: "100%", sm: 320 }, minWidth: 220 }}
          />
          <Button variant="contained" onClick={loadReport} disabled={loading}>
            {loading ? <CircularProgress size={20} color="inherit" /> : "Load Report"}
          </Button>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={18} /> : <FileDownloadIcon />}
            onClick={handleDownload}
            disabled={downloading || loading || reportData.length === 0}
          >
            Download
          </Button>
        </Box>
      </Paper>

      {reportData.length > 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          {uniqueCaseCount} active case{uniqueCaseCount === 1 ? "" : "s"} · {reportData.length} task
          {reportData.length === 1 ? "" : "s"}
        </Typography>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: "action.hover" }}>
              <TableCell sx={{ fontWeight: 600, width: 48 }}>No.</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 200 }}>Case Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>RAA</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>AP</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>ACL Date</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>LLO Expiration</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Negotiation</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Stage</TableCell>
              <TableCell sx={{ fontWeight: 600, whiteSpace: "nowrap" }}>Due Date</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 180 }}>Task</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Task Assignee</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 260 }}>Description</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={32} />
                </TableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  {reportData.length === 0
                    ? "Click Load Report to view LLO funnel tasks."
                    : "No tasks match the selected filters."}
                </TableCell>
              </TableRow>
            ) : (
              paginatedRows.map((row) => (
                <TableRow key={row.taskId} hover>
                  {!row.skipCaseColumns ? (
                    <>
                      <TableCell rowSpan={row.groupRowSpan} sx={matterGroupCellStyle}>
                        {row.rowNumber}
                      </TableCell>
                      <TableCell rowSpan={row.groupRowSpan} sx={{ ...matterGroupCellStyle, maxWidth: 240 }}>
                        {row.caseName}
                      </TableCell>
                      <TableCell rowSpan={row.groupRowSpan} sx={matterGroupCellStyle}>
                        {row.raa}
                      </TableCell>
                      <TableCell rowSpan={row.groupRowSpan} sx={matterGroupCellStyle}>
                        {row.ap}
                      </TableCell>
                      <TableCell rowSpan={row.groupRowSpan} sx={{ ...matterGroupCellStyle, whiteSpace: "nowrap" }}>
                        {row.aclDate || "—"}
                      </TableCell>
                      <TableCell rowSpan={row.groupRowSpan} sx={{ ...matterGroupCellStyle, whiteSpace: "nowrap" }}>
                        {row.lloExpiration || "—"}
                      </TableCell>
                      <TableCell rowSpan={row.groupRowSpan} sx={matterGroupCellStyle}>
                        {row.negotiation}
                      </TableCell>
                    </>
                  ) : null}
                  <TableCell sx={{ fontSize: "0.8125rem" }}>{row.stage}</TableCell>
                  <TableCell sx={{ whiteSpace: "nowrap" }}>
                    {row.dueDate ? dayjs(row.dueDate).format("MM/DD/YYYY") : "—"}
                  </TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>{row.taskName}</TableCell>
                  <TableCell>{row.taskAssignee}</TableCell>
                  <TableCell sx={{ whiteSpace: "pre-wrap", fontSize: "0.8125rem" }}>
                    {row.description}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={caseGroups.length}
          rowsPerPage={rowsPerPage}
          page={page}
          labelRowsPerPage="Cases per page"
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </TableContainer>
    </Box>
  );
}
